// ---------------------------------------------------------------------------
// Pre-Sales technical progress: how far the quotation preparation of one assignment has come, from recorded work
// only. Acceptance 10 % + technical deliverables 70 % (the existing technical readiness score, with its configured
// deliverable weights) + Pre-Sales tasks 20 %. Capped at 95 % until a valid Quotation Ready. A separate schedule marker
// (expected progress from the start and deadline dates) is shown only when both dates are reliable.
// Not Deal Health and not CRM compliance: Pre-Sales work only.
// ---------------------------------------------------------------------------
import type { PresalesStatus } from './dealDocuments';

export const PROGRESS_WEIGHTS = { acceptance: 10, deliverables: 70, tasks: 20 } as const;
/** Until Quotation Ready is valid, the bar never shows done. */
export const PROGRESS_CAP = 95;
/** The tasks part can't run ahead of the deliverables by more than this share (many small tasks don't inflate it). */
export const TASKS_LEAD = 0.25;

export interface ProgressInput {
  today: string;
  status: PresalesStatus | null;
  /** The lead engineer accepted the assignment (the latest hand-off acknowledged). */
  acceptedAt: string | null;
  /** The latest hand-off (when the assignment was given). */
  handedAt: string | null;
  /** Internal quotation deadline, else the customer submission deadline. */
  quoteDeadline: string | null; submissionDeadline: string | null;
  /** The existing technical readiness result (dealEngine.technicalReadiness) for the current revision. */
  readiness: { tracked: boolean; score: number | null; systems: { system: string; brand: string; score: number; missing: string[] }[] };
  /** Confirmed Our Quoted Brands lines of the current revision, and whether brands are marked not applicable. */
  brandLines: number; brandNotApplicable: boolean; currentRevision: string | null;
  /** Relevant technical tasks of the lead and support engineers on the deal (administrative and automatic ones left out). */
  tasks: { total: number; done: number; overdue: number; overdueList?: { subject: string; dueDate: string; owner: string }[] };
  /** A costing workbook was read successfully at least once (the linked costing import, not a file name). */
  costingRead?: boolean;
  /** Why the costing workbook couldn't be read (blocks a valid Quotation Ready). */
  costingProblem: string | null;
  clarificationNote: string | null;
  /** After the quotation: open customer submittal requests, submittals to revise (tracked apart). */
  openRequests: number; toRevise: number;
}

export type ProgressState = 'not_accepted' | 'in_progress' | 'paused' | 'ready' | 'ready_check';
/** One thing to fix or know, as a short chip with its detail and the corrective action. */
export interface ProgressIssue {
  key: 'not_accepted' | 'no_revision' | 'no_brand_lines' | 'statuses_not_set' | 'deliverables_missing' | 'overdue_tasks' | 'clarification' | 'costing_not_read' | 'ready_check';
  /** block: holds the work; warn: to fix; info: to know. */
  level: 'block' | 'warn' | 'info';
  label: string; detail: string;
  action: 'accept' | 'quoted_brands' | 'read_costing' | 'tasks' | null;
}
export interface TechnicalProgress {
  state: ProgressState;
  /** 0–100; with insufficient tracking it is only what is known (acceptance and tasks), flagged not reliable. */
  percent: number;
  reliable: boolean;
  /** Insufficient Tracking Data: what is missing to measure the deliverables. */
  insufficient: string[] | null;
  parts: { acceptance: number; deliverables: number | null; tasks: number | null };
  /** Missing deliverables per system (from the readiness engine). */
  missing: { system: string; brand: string; items: string[] }[];
  tasks: { total: number; done: number; overdue: number; overdueList?: { subject: string; dueDate: string; owner: string }[] };
  blockers: string[];
  /** Short labels with details and corrective actions (most important first). */
  issues: ProgressIssue[];
  /** Quotation Ready that doesn't meet its requirements yet (stays at 95 %). */
  readyChecks: string[];
  /** Schedule, not completion: null when the dates are missing or unreliable. */
  pace: { start: string; deadline: string; expected: number; variance: number | null; daysRemaining: number; overdue: boolean } | null;
  /** After the quotation, separate from this progress. */
  postQuotation: { openRequests: number; toRevise: number };
}

const dayNum = (d: string) => Math.round(Date.parse(`${d.slice(0, 10)}T00:00:00Z`) / 86_400_000);
const isDay = (d: string | null): d is string => !!d && /^\d{4}-\d{2}-\d{2}/.test(d) && !Number.isNaN(Date.parse(`${d.slice(0, 10)}T00:00:00Z`));

/** What a valid Quotation Ready needs: the quoted systems recorded (or brands not applicable) and a readable costing workbook. */
export function quotationReadyChecks(i: Pick<ProgressInput, 'brandLines' | 'brandNotApplicable' | 'costingProblem'>): string[] {
  const out: string[] = [];
  if (!i.brandLines && !i.brandNotApplicable) out.push('Our Quoted Brands: no confirmed system for the current revision (or mark brands not applicable)');
  if (i.costingProblem) out.push(`Costing workbook not read: ${i.costingProblem}`);
  return out;
}

export function technicalProgress(i: ProgressInput): TechnicalProgress {
  const accepted = !!i.acceptedAt || i.status === 'in_progress' || i.status === 'clarification' || i.status === 'quotation_ready';
  const tracked = i.readiness.tracked && i.readiness.score != null;
  const readiness = tracked ? i.readiness.score! / 100 : null;
  const acceptance = accepted ? PROGRESS_WEIGHTS.acceptance : 0;
  const deliverables = readiness == null ? null : PROGRESS_WEIGHTS.deliverables * readiness;
  // Tasks: completed share of the relevant tasks, never more than the deliverables + 25 points. No task yet: the part
  // follows the deliverables (no penalty, no bonus).
  const ratio = i.tasks.total ? i.tasks.done / i.tasks.total : null;
  const capped = ratio == null ? readiness : readiness == null ? ratio : Math.min(ratio, readiness + TASKS_LEAD);
  const tasks = capped == null ? null : PROGRESS_WEIGHTS.tasks * capped;

  const readyChecks = i.status === 'quotation_ready' ? quotationReadyChecks(i) : [];
  const raw = acceptance + (deliverables ?? 0) + (tasks ?? 0);
  const valid = i.status === 'quotation_ready' && !readyChecks.length;
  const percent = valid ? 100 : Math.min(PROGRESS_CAP, Math.round(raw));

  const insufficient = !tracked && !valid ? [
    !i.currentRevision ? 'No quotation revision set on the deal' : null,
    !i.brandLines ? 'No confirmed Our Quoted Brands lines for the current revision' : 'Deliverable statuses (BOQ, design, drawings, compliance, approval, vendor quote, costing) not set on any system',
  ].filter((x): x is string => !!x) : null;

  const state: ProgressState = !accepted && i.status === 'ready' ? 'not_accepted'
    : i.status === 'clarification' ? 'paused'
      : i.status === 'quotation_ready' ? (valid ? 'ready' : 'ready_check') : 'in_progress';

  // Only real waiting holds the work. A costing workbook not read yet is information until Quotation Ready.
  const blockers = [i.status === 'clarification' ? `Waiting for clarification${i.clarificationNote ? `: ${i.clarificationNote}` : ''}` : null]
    .filter((x): x is string => !!x);
  const missing = i.readiness.systems.filter((s) => s.missing.length).map((s) => ({ system: s.system, brand: s.brand, items: s.missing }));
  const missingCount = missing.reduce((n, m) => n + m.items.length, 0);
  const issues: ProgressIssue[] = [];
  if (i.status === 'clarification') issues.push({ key: 'clarification', level: 'block', label: 'Waiting for clarification', detail: i.clarificationNote || 'Documents or answers asked from sales (no details written).', action: null });
  if (!accepted && i.status === 'ready') issues.push({ key: 'not_accepted', level: 'warn', label: 'Not accepted', detail: 'The lead engineer hasn’t started the assignment yet: acceptance counts 10 %.', action: 'accept' });
  for (const c of readyChecks) issues.push({ key: 'ready_check', level: 'warn', label: 'Quotation Ready: to check', detail: c, action: /Costing/.test(c) ? 'read_costing' : 'quoted_brands' });
  if (!valid) {
    if (!i.currentRevision) issues.push({ key: 'no_revision', level: 'warn', label: 'No revision', detail: 'No quotation revision on the deal. The first Our Quoted Brands line (R0) or a costing workbook read sets it.', action: 'quoted_brands' });
    if (!i.brandLines && !i.brandNotApplicable) issues.push({ key: 'no_brand_lines', level: 'warn', label: 'No Our Quoted Brands', detail: 'No confirmed system for the current revision: add the systems and brands, or read the costing workbook (it fills them), or mark brands not applicable.', action: 'quoted_brands' });
    else if (i.brandLines && !tracked) issues.push({ key: 'statuses_not_set', level: 'warn', label: 'Deliverable statuses not set', detail: 'Set the status of BOQ, design, drawings, compliance, approval, vendor quotation and costing on each system in Our Quoted Brands.', action: 'quoted_brands' });
    if (missingCount) issues.push({ key: 'deliverables_missing', level: 'warn', label: `${missingCount} deliverable${missingCount === 1 ? '' : 's'} missing`, detail: missing.map((m) => `${m.system}${m.brand ? ` (${m.brand})` : ''}: ${m.items.join(', ')}`).join(' · '), action: 'quoted_brands' });
  }
  if (i.tasks.overdue) issues.push({ key: 'overdue_tasks', level: 'warn', label: `${i.tasks.overdue} overdue task${i.tasks.overdue === 1 ? '' : 's'}`,
    detail: (i.tasks.overdueList ?? []).map((t) => `${t.subject} (${t.owner}, due ${t.dueDate})`).join(' · ') || 'Technical tasks of the engineers past their due day.', action: 'tasks' });
  if (i.costingRead === false && i.status !== 'quotation_ready') issues.push({ key: 'costing_not_read', level: 'info', label: 'Costing not read yet', detail: 'No costing workbook read for this deal yet: it is needed at Quotation Ready. Name it EST… / Pricing / Costing.', action: 'read_costing' });

  // Schedule marker: from the start (acceptance, else the hand-off) to the deadline. Hidden without reliable dates.
  const start = isDay(i.acceptedAt) ? i.acceptedAt.slice(0, 10) : isDay(i.handedAt) ? i.handedAt.slice(0, 10) : null;
  const deadline = isDay(i.quoteDeadline) ? i.quoteDeadline.slice(0, 10) : isDay(i.submissionDeadline) ? i.submissionDeadline.slice(0, 10) : null;
  let pace: TechnicalProgress['pace'] = null;
  if (start && deadline && dayNum(deadline) > dayNum(start) && state !== 'ready') {
    const span = dayNum(deadline) - dayNum(start);
    const gone = Math.max(0, dayNum(i.today) - dayNum(start));
    const expected = Math.round(Math.min(1, gone / span) * 100);
    const daysRemaining = dayNum(deadline) - dayNum(i.today);
    pace = { start, deadline, expected, variance: insufficient ? null : percent - expected, daysRemaining, overdue: daysRemaining < 0 };
  }

  return {
    state, percent, reliable: !insufficient, insufficient,
    parts: { acceptance, deliverables: deliverables == null ? null : Math.round(deliverables), tasks: tasks == null ? null : Math.round(tasks) },
    missing, tasks: i.tasks, blockers, issues, readyChecks, pace, postQuotation: { openRequests: i.openRequests, toRevise: i.toRevise },
  };
}
